import { ColumnDefinition, RowDefinition, SmallTitle, Table, TinyTitle, palettes } from '@indigo-ag/component-module';
import { ExploredAreas as IExploredAreas } from '@indigo-ag/schema';
import React, { useState } from 'react';

import i18next from 'core/i18n';
import {
  PhaseSection,
  PhaseSectionProps
} from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseSection';
import {
  EnhancedExpansionPanel,
  GridContainer,
  GridContainerItem,
  IExploredAreasInfo,
  createStylesHook,
  parseNumberToLocale
} from 'shared';
import { ExploredAreasModal, ExploredAreasModalProps } from './components/ExploredAreasModal/ExploredAreasModal';

export interface ExploredAreasProps
  extends Pick<PhaseSectionProps, 'disabled'>,
    Pick<ExploredAreasModalProps, 'updateCredit'> {
  exploredAreasInfo?: IExploredAreasInfo;
}

const useStyles = createStylesHook({
  card: {
    border: `1px solid ${palettes.brand.gray[400]}`,
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
    '& .title': {
      fontSize: 18,
      color: palettes.brand.blue.base,
      marginBottom: 6
    }
  }
});

interface FormattedExploredAreasData {
  id: string;
  numberExploredAreas: string;
  totalOfHa: string;
}

type ColumnId = 'numberExploredAreas' | 'totalOfHa';

const columnDefinition: ColumnDefinition<FormattedExploredAreasData, ColumnId>[] = [
  {
    id: 'numberExploredAreas',
    header: i18next.t('numberExploredAreas'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.numberExploredAreas} />
  },
  {
    id: 'totalOfHa',
    header: i18next.t('totalOfHa'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.totalOfHa} />
  }
];

const rowDefinition: RowDefinition<FormattedExploredAreasData, ColumnId> = {
  rowId: ({ row }) => row.id
};

interface ExploredAreasSectionSummaryProps extends Pick<ExploredAreasProps, 'exploredAreasInfo'> {}

export const ExploredAreasSectionSummary: React.FunctionComponent<ExploredAreasSectionSummaryProps> = ({
  exploredAreasInfo
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  const getTotalHa = (): number => {
    if (exploredAreasInfo?.exploredAreas?.length) {
      return exploredAreasInfo.exploredAreas.reduce((p, c) => p + c.registrationArea, 0);
    } else {
      return 0;
    }
  };

  const data = [
    {
      id: '1',
      numberExploredAreas: String(exploredAreasInfo?.exploredAreas?.length),
      totalOfHa: parseNumberToLocale(getTotalHa()) || ''
    }
  ];

  const getAreaGridItems = (area: IExploredAreas): GridContainerItem[] => {
    return [
      {
        label: i18next.t('registration'),
        value: area.registration
      },
      {
        label: i18next.t('registrationArea'),
        value: parseNumberToLocale(area.registrationArea)
      },
      {
        label: i18next.t('freeArea'),
        value: parseNumberToLocale(area.freeArea)
      },
      {
        label: i18next.t('municipality'),
        value: area.municipality
      },
      {
        label: i18next.t('county'),
        value: area.county
      },
      {
        label: 'UF',
        value: area.UF
      }
    ];
  };

  return exploredAreasInfo?.exploredAreas?.length ? (
    <div className="width-100">
      <Table label="Explored Areas" data={data} columnDefinition={columnDefinition} rowDefinition={rowDefinition} />
      <EnhancedExpansionPanel title={i18next.t('detailExploredAreas')}>
        <div className="width-100">
          {exploredAreasInfo.exploredAreas.map((area, i) => (
            <div className={classes.card} key={i}>
              <SmallTitle className="title">{area.name}</SmallTitle>
              <TinyTitle>{area.holder}</TinyTitle>
              <GridContainer columns={3} items={getAreaGridItems(area)} classes={{ container: 'mt-4' }} />
            </div>
          ))}
        </div>
      </EnhancedExpansionPanel>
    </div>
  ) : null;
};

export const ExploredAreas: React.FunctionComponent<ExploredAreasProps> = ({
  exploredAreasInfo,
  updateCredit,
  children,
  ...phaseSectionProps
}) => {
  /**
   * State
   */
  const [modalOpen, setModalOpen] = useState(false);

  const closeModal = (): void => setModalOpen(false);

  const openModal = (): void => setModalOpen(true);

  return (
    <>
      <PhaseSection
        {...phaseSectionProps}
        open={!!exploredAreasInfo}
        onClickEdit={openModal}
        title={i18next.t('exploredAreas')}
        subtitle={i18next.t('includesRequiredFields')}
        renderSeparatorBefore
      >
        <ExploredAreasSectionSummary exploredAreasInfo={exploredAreasInfo} />
      </PhaseSection>
      <ExploredAreasModal
        closeModal={closeModal}
        open={modalOpen}
        exploredAreasInfo={exploredAreasInfo}
        updateCredit={updateCredit}
      />
    </>
  );
};
